import { PlusIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { GuestPassVisit, useInvalidateGuestPassProfile } from '@/api/clients/get-guest-pass-profile'
import { VisitDetailsModal } from '@/components/Clients/ClientProfileView/VisitDetailsModal'
import { VisitCreateModal } from '@/components/Clients/VisitCreateModal/VisitCreateModal'
import { TableGroup } from '@/components/layout/table-group'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { PaginationFilters } from '@/components/ui/pagination'
import { RowAction } from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import { popupFactory } from '@/factories'

import * as S from './guest-passes.styled'
import { GuestPassesCheckinsProps } from './guest-passes.types'
import { getColumns } from './guest-passes.utils'

export const GuestPassesCheckins = <F extends PaginationFilters = PaginationFilters>({
  amenityClientId,
  clientId,
  visits,
  total,
  isLoading,
  tableFilters,
  timezone,
}: GuestPassesCheckinsProps<F>) => {
  const { t } = useTranslation(['clients', 'common'])
  const invalidateGuestPassProfile = useInvalidateGuestPassProfile()

  const handleAddCheckinClick = () =>
    popupFactory.open(VisitCreateModal as Parameters<typeof popupFactory.open>[0], {
      addGuestPass: async () => {
        invalidateGuestPassProfile()
      },
      amenityClientId,
      clientId,
    })

  const rowActions: RowAction<GuestPassVisit>[] = [
    {
      label: t('clients:guestPasses.checkins.viewDetails'),
      onClick: (visit) =>
        popupFactory.open(VisitDetailsModal as Parameters<typeof popupFactory.open>[0], {
          fetchGuestPassVisits: invalidateGuestPassProfile,
          guestPassVisit: visit,
          timezone,
        }),
      primary: true,
    },
  ]

  return (
    <S.CheckinsContainer>
      <TableGroup
        noShadow
        id="client-guest-passes-checkins"
        data={visits}
        loading={isLoading}
        columns={getColumns(t, timezone)}
        getRowKey={(visit) => String(visit.id)}
        tableFilters={tableFilters}
        pagination={{ paginationFilters: tableFilters, total }}
        enableColumnVisibility={false}
        rowActions={rowActions}
        navigation={{
          actions: (
            <Button size="small" onClick={handleAddCheckinClick}>
              {t('clients:guestPasses.checkins.addCheckin')}
              <Icon icon={PlusIcon} size="1.4rem" />
            </Button>
          ),
          statusTabs: (
            <Typography as="h2" size="heading-small" color="black800">
              {t('clients:guestPasses.checkins.title')}
            </Typography>
          ),
        }}
        emptyNoFilters={{
          description: t('clients:guestPasses.checkins.empty.noFilters.description'),
          title: t('clients:guestPasses.checkins.empty.noFilters.title'),
        }}
        emptyWithFilters={{
          description: t('common:table.emptyWithFilters.description'),
          title: t('common:table.emptyWithFilters.title'),
        }}
      />
    </S.CheckinsContainer>
  )
}
